/**
 * Colour-keyed, clickable legend for the standings chart. Clicking toggles users in the
 * head-to-head selection; the signed-in user is marked with "(you)".
 */
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  useColorMode,
  useColorModeValue,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';

import type { UserSeries } from '@/helpers/leagues/standingsSeries';
import { pageButtonProps } from '@/theme/sitePageTypography';
import { chartColor } from './chartPalette';

export interface StandingsChartLegendProps {
  series: UserSeries[];
  colorIndex: Map<string, number>;
  selectedUids: ReadonlySet<string>;
  currentUid?: string;
  onHoverUid: (uid: string | null) => void;
  onClickUid: (uid: string) => void;
  onClearSelection: () => void;
}

export function StandingsChartLegend(props: StandingsChartLegendProps) {
  const { colorMode } = useColorMode();
  const clearDividerColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const hasFilter = props.selectedUids.size > 0;

  return (
    <Wrap spacing={2} aria-label='Chart legend'>
      {props.series.map((s) => {
        const idx = props.colorIndex.get(s.uid) ?? 0;
        const color = chartColor(idx, colorMode);
        const isSelected = props.selectedUids.has(s.uid);
        const isCurrent = props.currentUid === s.uid;
        return (
          <WrapItem key={s.uid}>
            <Flex
              as='button'
              type='button'
              align='center'
              gap={2}
              px={2}
              py={1}
              borderRadius='md'
              borderWidth='1px'
              borderColor={isSelected ? color : 'transparent'}
              bg={isSelected ? (colorMode === 'light' ? 'gray.100' : 'whiteAlpha.200') : 'transparent'}
              opacity={hasFilter && !isSelected ? 0.55 : 1}
              transition='opacity 0.12s, border-color 0.12s'
              onMouseEnter={() => props.onHoverUid(s.uid)}
              onMouseLeave={() => props.onHoverUid(null)}
              onClick={() => props.onClickUid(s.uid)}
            >
              <Box w='12px' h='12px' borderRadius='full' bg={color} flexShrink={0} />
              <HStack spacing={1} minW={0}>
                <Text fontSize='xs' fontWeight={isCurrent ? 700 : 500} noOfLines={1}>
                  {s.name}
                </Text>
                {isCurrent ? (
                  <Text fontSize='2xs' color='green.500' fontWeight={700}>
                    (you)
                  </Text>
                ) : null}
                <Text fontSize='2xs' color='gray.500'>
                  {s.finalScore}
                </Text>
              </HStack>
            </Flex>
          </WrapItem>
        );
      })}
      {hasFilter ? (
        <WrapItem ms={{ base: 2, md: 4 }} pl={3} borderLeftWidth='1px' borderLeftColor={clearDividerColor} alignSelf='center'>
          <Button
            {...pageButtonProps}
            variant='ghost'
            fontSize='xs'
            color='gray.500'
            onClick={props.onClearSelection}
          >
            Clear
          </Button>
        </WrapItem>
      ) : null}
    </Wrap>
  );
}
